import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  createInsightStream,
  createInterpretStream,
  createCrossref,
  getCrossref,
  uploadDocument,
  analyzeDocument,
  getInsight,
  getInterpret,
  isUrl,
  normalizeUrl,
  loadMemory,
  saveToMemory,
  clearMemory,
  loadSettings,
  saveSettings,
  shareText,
  getUserMemory,
  addUserMemory,
  clearUserMemory,
} from "@/lib/rexxponsApi";
import { TopBar } from "@/components/rexxpons/TopBar";
import { Landing } from "@/components/rexxpons/Landing";
import { Orchestration } from "@/components/rexxpons/Orchestration";
import { CognitiveReader } from "@/components/rexxpons/CognitiveReader";
import { CrossRefDocument } from "@/components/rexxpons/CrossRefDocument";
import { MemoryPanel } from "@/components/rexxpons/MemoryPanel";
import { ContactModal } from "@/components/rexxpons/ContactModal";
import { useAuth } from "@/components/rexxpons/AuthContext";
import { InsightDocument } from "@/components/lonero/InsightDocument";

function App() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const handleErr = useCallback(
    (e, fallback) => {
      setView("landing");
      if (e?.response?.status === 402) {
        toast.error(e.response.data?.detail || "This is a Pro feature. Upgrade to unlock it.");
        navigate("/pricing");
        return;
      }
      toast.error(e?.response?.data?.detail || e?.message || fallback);
    },
    [navigate]
  );
  const [view, setView] = useState("landing"); // landing | loading | insight | reader
  const [kind, setKind] = useState("search");
  const [activeInput, setActiveInput] = useState("");
  const [phase, setPhase] = useState("");
  const [insightDoc, setInsightDoc] = useState(null);
  const [readerDoc, setReaderDoc] = useState(null);
  const [crossrefDoc, setCrossrefDoc] = useState(null);
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [memory, setMemory] = useState([]);
  const [settings, setSettings] = useState(loadSettings());
  const [engines, setEngines] = useState(null);

  useEffect(() => {
    if (user) {
      getUserMemory().then(setMemory).catch(() => setMemory([]));
    } else {
      setMemory(loadMemory());
    }
  }, [user]);

  const remember = useCallback(
    async (entry) => {
      if (user) {
        try {
          const items = await addUserMemory(entry);
          setMemory(items);
          return;
        } catch (e) {
          console.error("Saving memory to account failed, using local storage", e);
        }
      }
      setMemory(saveToMemory(entry));
    },
    [user]
  );

  const updateSettings = useCallback((s) => {
    setSettings(s);
    saveSettings(s);
  }, []);

  const entitled = !!(user && (user.plan === "pro" || user.plan === "team"));
  const promptUpgrade = useCallback(
    (label) => {
      toast.info(`${label} reasoning is a Pro feature. Upgrade to unlock advanced reasoning depth.`);
      navigate("/pricing");
    },
    [navigate]
  );

  useEffect(() => {
    if (!entitled && (settings.reasoning === "Deep" || settings.reasoning === "Critical")) {
      updateSettings({ ...settings, reasoning: "Balanced" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entitled]);

  const goHome = useCallback(() => {
    setView("landing");
    setInsightDoc(null);
    setReaderDoc(null);
    setCrossrefDoc(null);
    setActiveInput("");
    setPhase("");
    setEngines(null);
    window.scrollTo({ top: 0 });
  }, []);

  const runCrossref = useCallback(
    async (urls) => {
      setKind("interpret");
      setActiveInput(`${urls.length} sources`);
      setPhase("Fetching the sources");
      setView("loading");
      window.scrollTo({ top: 0 });

      const langParam = settings.language === "Auto" ? "auto" : settings.language;
      const cmd = { language: langParam, reasoning: settings.reasoning, attitude: settings.attitude };
      const onProgress = (p) => p?.phase && setPhase(p.phase);

      try {
        const doc = await createCrossref(urls, { onProgress, ...cmd });
        setCrossrefDoc(doc);
        remember({ id: doc.id, type: "crossref", title: doc.title, subtitle: `${doc.sources.length} sources`, created_at: doc.created_at });
        setView("crossref");
        window.scrollTo({ top: 0 });
      } catch (e) {
        handleErr(e, "Couldn't cross-reference those sources. Please try again.");
      }
    },
    [settings, remember, handleErr]
  );

  const run = useCallback(
    async (rawInput) => {
      const asUrl = isUrl(rawInput);
      setKind(asUrl ? "interpret" : "search");
      setActiveInput(asUrl ? normalizeUrl(rawInput) : rawInput);
      setPhase("");
      setEngines(null);
      setView("loading");
      window.scrollTo({ top: 0 });

      const onProgress = (p) => { if (p?.phase) setPhase(p.phase); if ("engines" in (p || {})) setEngines(p.engines || null); };
      const langParam = settings.language === "Auto" ? "auto" : settings.language;
      const cmd = { language: langParam, reasoning: settings.reasoning, attitude: settings.attitude };

      try {
        if (asUrl) {
          const doc = await createInterpretStream(normalizeUrl(rawInput), { onProgress, ...cmd });
          setReaderDoc(doc);
          remember({ id: doc.id, type: "interpret", title: doc.title, subtitle: doc.url, created_at: doc.created_at });
          setView("reader");
        } else {
          const doc = await createInsightStream(rawInput, langParam, { onProgress, reasoning: settings.reasoning, attitude: settings.attitude });
          setInsightDoc(doc);
          remember({ id: doc.id, type: "search", title: doc.title, subtitle: doc.query, created_at: doc.created_at });
          setView("insight");
        }
        window.scrollTo({ top: 0 });
      } catch (e) {
        handleErr(e, "Something interrupted the process. Please try again.");
      }
    },
    [settings, remember, handleErr]
  );

  const runUpload = useCallback(
    async (file) => {
      setKind("interpret");
      setActiveInput(file.name);
      setPhase("Uploading document");
      setView("loading");
      window.scrollTo({ top: 0 });

      const langParam = settings.language === "Auto" ? "auto" : settings.language;
      const cmd = { language: langParam, reasoning: settings.reasoning, attitude: settings.attitude };
      const onProgress = (p) => p?.phase && setPhase(p.phase);

      try {
        const { file_id } = await uploadDocument(file);
        const doc = await analyzeDocument(file_id, { onProgress, ...cmd });
        setReaderDoc(doc);
        remember({ id: doc.id, type: "document", title: doc.title, subtitle: file.name, created_at: doc.created_at });
        setView("reader");
        window.scrollTo({ top: 0 });
      } catch (e) {
        handleErr(e, "Couldn't analyze that document. Please try again.");
      }
    },
    [settings, remember, handleErr]
  );

  const openFromMemory = useCallback(async (item) => {
    setMemoryOpen(false);
    const isReader = item.type === "interpret" || item.type === "document";
    setKind(isReader ? "interpret" : "search");
    setActiveInput(item.subtitle);
    setPhase("Recalling from memory");
    setView("loading");
    try {
      if (item.type === "crossref") {
        const doc = await getCrossref(item.id);
        setCrossrefDoc(doc);
        setView("crossref");
      } else if (isReader) {
        const doc = await getInterpret(item.id);
        setReaderDoc(doc);
        setView("reader");
      } else {
        const doc = await getInsight(item.id);
        setInsightDoc(doc);
        setView("insight");
      }
      window.scrollTo({ top: 0 });
    } catch {
      toast.error("Couldn't recall that from memory.");
      setView("landing");
    }
  }, []);

  const handleClear = useCallback(async () => {
    if (user) {
      try { await clearUserMemory(); } catch (e) { console.error("Clearing account memory failed", e); toast.error("Could not clear memory on your account"); }
      setMemory([]);
      return;
    }
    clearMemory();
    setMemory([]);
  }, [user]);

  const onShare = useCallback(async (text) => {
    const ok = await shareText(text);
    toast[ok ? "success" : "error"](ok ? "Insight copied to clipboard" : "Couldn't share this insight.");
  }, []);

  return (
    <div className="App nexx-ambient min-h-screen bg-background text-foreground">
      <Toaster position="top-center" theme="dark" />
      <TopBar onLogoClick={goHome} onSearch={run} onOpenMemory={() => setMemoryOpen(true)} onContact={() => setContactOpen(true)} memoryCount={memory.length} settings={settings} onSettingsChange={updateSettings} />

      <main className="relative z-10">
        {view === "landing" && <Landing onSubmit={run} onUpload={runUpload} onCrossref={runCrossref} settings={settings} onSettingsChange={updateSettings} entitled={entitled} onUpgrade={promptUpgrade} />}
        {view === "loading" && <Orchestration query={activeInput} phase={phase} kind={kind} engines={engines} />}
        {view === "insight" && insightDoc && <InsightDocument doc={insightDoc} onBack={goHome} onShare={onShare} />}
        {view === "reader" && readerDoc && <CognitiveReader doc={readerDoc} onBack={goHome} onShare={onShare} />}
        {view === "crossref" && crossrefDoc && <CrossRefDocument doc={crossrefDoc} onBack={goHome} onShare={onShare} />}
      </main>

      <MemoryPanel
        open={memoryOpen}
        items={memory}
        onClose={() => setMemoryOpen(false)}
        onSelect={openFromMemory}
        onClear={handleClear}
      />
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
}

export default App;
